import styled from 'styled-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import mapboxgl from 'mapbox-gl';
import { Button, ButtonGroup } from 'reactstrap';
import useInitializeMapEffect from '../../hooks/useInitializeMapEffect';
import { CONUS_CENTER } from '../../constants/coords';
import { coordsObjToPoint } from '../../utils/geojson';
import MapContainer from '../map/MapContainer';
import { useMapCenter } from '../fields-preview/utils/map';
import PositionedOverMap from '../basics/PositionedOverMap';

const MinimapContainer = styled(MapContainer)`
  width: 100%;
  height: 200px;
  margin-bottom: 1rem;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;

  // Fix Mapbox logo rendering
  .mapboxgl-ctrl-logo.mapboxgl-compact {
    width: 88px;
  }
`;

const DEFAULT_ZOOM = 15;

const MapCenterMinimap = () => {
  // Map state
  const minimapContainer = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const loadedMap = mapLoaded && mapRef.current;

  const { values, setFieldValue } = useFormikContext();
  const centerFeature = coordsObjToPoint(values.mapCenter);

  const [lng, lat] = useMapCenter(mapLoaded, mapRef);
  const showButton =
    mapLoaded &&
    centerFeature &&
    (values.mapCenter.lng !== lng || values.mapCenter.lat !== lat);

  const marker = useMemo(() => new mapboxgl.Marker({ color: '#0088FE' }), []);
  const moveMarkerToCenter = () => marker.setLngLat(mapRef.current.getCenter());

  // Initialize the map
  useInitializeMapEffect({
    mapContainer: minimapContainer,
    mapRef,
    mapLoaded,
    setMapLoaded,
    onLoad: () => {
      marker.setLngLat(mapRef.current.getCenter()).addTo(mapRef.current);
      mapRef.current.on('move', moveMarkerToCenter);
    },
    onUnload: () => {
      marker.remove();
      mapRef.current.off('move', moveMarkerToCenter);
    },
    ...(centerFeature ? values.mapCenter : CONUS_CENTER),
    controls: {
      geolocation: false,
      compass: false,
      fullscreen: false,
    },
    zoom: DEFAULT_ZOOM,
  });

  // Set center and zoom of map when user changes lat/lng
  useEffect(() => {
    if (!loadedMap) return;
    if (centerFeature) {
      mapRef.current.resize();
      mapRef.current.setCenter([values.mapCenter.lng, values.mapCenter.lat]);
      mapRef.current.setZoom(DEFAULT_ZOOM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.mapCenter]);

  return (
    <div className="h-100 w-100 position-relative">
      <MinimapContainer
        ref={minimapContainer}
        className={centerFeature ? '' : 'd-none'}
      />
      <PositionedOverMap placement="top left">
        {showButton && (
          <ButtonGroup>
            <Button
              size="sm"
              color="primary"
              onClick={() =>
                // Set form values for lat/lng
                setFieldValue('mapCenter', { lat, lng })
              }
            >
              Set Map Center
            </Button>
            <Button
              size="sm"
              color="light"
              onClick={() =>
                // Reset map center to form values
                mapRef.current.setCenter([
                  values.mapCenter.lng,
                  values.mapCenter.lat,
                ])
              }
            >
              Reset
            </Button>
          </ButtonGroup>
        )}
      </PositionedOverMap>
      {!centerFeature && (
        <div className="p-3 margin-auto text-center text-muted">
          Choose an address or enter coordinates to view map
        </div>
      )}
    </div>
  );
};

export default MapCenterMinimap;
